import {
  Shape,
  SpotAppearance,
  SpotShape,
  SpotShapeItem,
  SpotShapeType
} from '../types';

/**
 * The spot's effective appearance — what actually drives its rendered look,
 * label placement, etc. `spotAppearance` wins when explicitly set;
 * otherwise falls back to the spot's own `spotType`, EXCEPT for parking
 * (parking_lot/parking_space): a parking spot's default look is a plain
 * Node dot, not its own oversized/redundant "parking" square — Parking/
 * Parking Space aren't even selectable Appearance options anymore (see the
 * spot panel's Appearance dropdown), so "Default" must mean Node for these
 * two types, for every spot regardless of when it was created/last edited.
 *
 * Use this instead of the raw `spotAppearance || spotType` fallback
 * wherever a spot's rendered look/geometry is decided.
 */
export const getEffectiveSpotAppearance = (
  spotType: SpotShapeType,
  spotAppearance?: SpotAppearance
): SpotAppearance => {
  if (spotAppearance) return spotAppearance;
  if (
    spotType === SpotShapeType.Parking ||
    spotType === SpotShapeType.ParkingSpace
  ) {
    return SpotShapeType.Node;
  }
  return spotType;
};

export const getSpotsWithoutResourceId = (shapes: Shape[]): SpotShapeItem[] => {
  return shapes.flatMap((shape) => {
    if (shape.type === 'spot' && !shape.resource_id) {
      return shape;
    }
    if (shape.type === 'group' && Array.isArray(shape.children)) {
      return getSpotsWithoutResourceId(shape.children);
    }
    return [];
  });
};

export const getSpotsWithResourceId = (shapes: Shape[]): SpotShapeItem[] => {
  return shapes.flatMap((shape) => {
    if (shape.type === 'spot' && shape.resource_id) {
      return shape;
    }
    if (shape.type === 'group' && Array.isArray(shape.children)) {
      return getSpotsWithResourceId(shape.children);
    }
    return [];
  });
};

export const getSpotNameFromSpotShapeItem = (spotItem: SpotShape) => {
  return spotItem.label;
};

export const assignResourceIdToSpotId = (
  shapes: Shape[],
  spotId: string,
  resource_id: string
): Shape[] => {
  return shapes.map((shape) => {
    if (shape.type === 'spot' && shape.id === spotId) {
      return { ...shape, resource_id };
    }

    if (shape.type === 'group' && Array.isArray(shape.children)) {
      return {
        ...shape,
        children: assignResourceIdToSpotId(shape.children, spotId, resource_id)
      };
    }

    return shape;
  });
};

export const assignLabelToSpotWithResourceId = (
  shapes: Shape[],
  resource_id: string,
  label: string
): Shape[] => {
  return shapes.map((shape) => {
    if (shape.type === 'spot' && shape.resource_id === resource_id) {
      return { ...shape, label };
    }

    if (shape.type === 'group' && Array.isArray(shape.children)) {
      return {
        ...shape,
        children: assignLabelToSpotWithResourceId(
          shape.children,
          resource_id,
          label
        )
      };
    }

    return shape;
  });
};

export const assignNameToSpotWithResourceId = (
  shapes: Shape[],
  resource_id: string,
  name: string
): Shape[] => {
  return shapes.map((shape) => {
    if (shape.type === 'spot' && shape.resource_id === resource_id) {
      return { ...shape, name };
    }

    if (shape.type === 'group' && Array.isArray(shape.children)) {
      return {
        ...shape,
        children: assignLabelToSpotWithResourceId(
          shape.children,
          resource_id,
          name
        )
      };
    }

    return shape;
  });
};

/**
 * Builds a resource_id → { label, name } map from spot shapes (recursing into
 * groups). Used to keep the resources cache in sync with labels/names edited in
 * the map builder, so a post-save recompute doesn't revert them to stale values.
 */
export const getSpotLabelsByResourceId = (
  shapes: Shape[]
): Map<string, { label: string; name?: string }> => {
  const result = new Map<string, { label: string; name?: string }>();

  const visit = (items: Shape[]) => {
    for (const shape of items) {
      if (shape.type === 'spot' && shape.resource_id) {
        result.set(shape.resource_id, { label: shape.label, name: shape.name });
      }

      if (shape.type === 'group' && Array.isArray(shape.children)) {
        visit(shape.children);
      }
    }
  };

  visit(shapes);
  return result;
};

export const getDeletedSpotsWithResources = (
  originalShapes: SpotShape[],
  newShapes: Shape[]
): SpotShape[] => {
  const originalSpots = getSpotsWithResourceId(originalShapes);
  const newSpots = getSpotsWithResourceId(newShapes);

  return originalSpots.filter((originalSpot) => {
    return !newSpots.find((newSpot) => newSpot.id === originalSpot.id);
  }) as SpotShape[];
};
